import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ModalShell } from "@/components/ui/modal-shell";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Plus, Pencil, Trash2, X, ChevronDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { extractApiErrorMessage } from "@/lib/api-error";
import { formatCurrency } from "@/lib/utils";
import { IngredientSelectField } from "./IngredientSelectField";

export interface TemplateOptionItem {
  name: string;
  price: number;
}

export type TemplatePriceMode = "sum" | "highest" | "average";
export type TemplateOptionType = "personalizado" | "ingredientes";

export interface OptionGroupTemplate {
  id: string;
  storeId: string;
  name: string;
  selectionType: "single" | "multiple";
  maxSelections: number | null;
  // Piso do grupo (só vem preenchido junto com `required`).
  minSelections: number | null;
  required: boolean;
  onePerOption: boolean;
  priceMode: TemplatePriceMode;
  optionType: TemplateOptionType;
  options: Array<TemplateOptionItem & { ingredientId?: string | null }>;
  sortOrder: number;
}

export interface CopiedGroup {
  name: string;
  selectionType: "single" | "multiple";
  maxSelections: string;
  minSelections: string;
  required: boolean;
  onePerOption: boolean;
  priceMode: TemplatePriceMode;
  optionType: TemplateOptionType;
  options: { name: string; price: string; ingredientId?: string }[];
}

const QUERY_KEY = ["option-group-templates"];

function listTemplates(): Promise<OptionGroupTemplate[]> {
  return customFetch<OptionGroupTemplate[]>("/api/option-group-templates", { method: "GET" });
}

interface PickerProps {
  onCreateBlank: () => void;
  onSelectTemplate: (group: CopiedGroup) => void;
}

export function OptionGroupTemplatesPicker({ onCreateBlank, onSelectTemplate }: PickerProps) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<OptionGroupTemplate | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();

  const { data: templates = [], isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: listTemplates,
  });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      customFetch<{ success: boolean }>(`/api/option-group-templates/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({ title: "Template excluído!" });
    },
    onError: (err: any) => {
      toast({ title: "Erro ao excluir", description: extractApiErrorMessage(err, "Tente novamente."), variant: "destructive" });
    },
  });

  const handleSelect = (tpl: OptionGroupTemplate) => {
    const copy: CopiedGroup = {
      name: tpl.name,
      selectionType: tpl.selectionType,
      maxSelections: tpl.maxSelections ? String(tpl.maxSelections) : "",
      // Piso só viaja com o obrigatório (o servidor já grava assim; aqui é o
      // mesmo cinto do lado do cliente).
      minSelections: tpl.required && tpl.minSelections ? String(tpl.minSelections) : "",
      required: tpl.required,
      onePerOption: tpl.onePerOption ?? false,
      priceMode: tpl.priceMode || "sum",
      optionType: tpl.optionType || "personalizado",
      options: tpl.options.map((o) => ({
        name: o.name,
        price: String(o.price ?? 0),
        ingredientId: o.ingredientId ?? undefined,
      })),
    };
    onSelectTemplate(copy);
    setOpen(false);
  };

  const handleDelete = async (tpl: OptionGroupTemplate) => {
    if (await confirm({
      title: `Excluir o template "${tpl.name}"?`,
      description: "Os produtos que já o usaram mantêm o grupo intacto.",
      confirmLabel: "Excluir",
    })) {
      deleteMutation.mutate(tpl.id);
    }
  };

  const handleEdit = (tpl: OptionGroupTemplate) => {
    setEditing(tpl);
    setEditorOpen(true);
    setOpen(false);
  };

  return (
    <>
      <div className="relative" ref={wrapRef}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            // Sem template salvo, o dropdown só teria "Criar novo grupo" — então
            // o primeiro clique já cria direto, sem o passo extra.
            if (!isLoading && templates.length === 0) {
              onCreateBlank();
            } else {
              setOpen((v) => !v);
            }
          }}
          data-testid="option-group-templates-picker-trigger"
        >
          <Plus className="w-3 h-3 mr-1" /> Adicionar grupo
          {(isLoading || templates.length > 0) && <ChevronDown className="w-3 h-3 ml-1" />}
        </Button>
        {open && (
          <div className="absolute right-0 z-30 mt-1 w-72 rounded-md border border-border bg-popover shadow-lg overflow-hidden">
            <button
              type="button"
              onClick={() => { onCreateBlank(); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent text-left border-b border-border"
              data-testid="option-group-templates-create-blank"
            >
              <Plus className="w-3.5 h-3.5" />
              <span className="font-medium">Criar novo grupo</span>
            </button>
            {isLoading ? (
              <div className="px-3 py-2 text-xs text-muted-foreground">Carregando templates...</div>
            ) : templates.length === 0 ? (
              <div className="px-3 py-2 text-xs text-muted-foreground">Nenhum template salvo ainda.</div>
            ) : (
              <div className="max-h-72 overflow-y-auto">
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground bg-muted/40">
                  Usar template salvo
                </div>
                {templates.map((tpl) => (
                  <div
                    key={tpl.id}
                    className="flex items-center gap-1 px-2 py-1.5 hover:bg-accent/50 border-b border-border/50 last:border-b-0"
                  >
                    <button
                      type="button"
                      onClick={() => handleSelect(tpl)}
                      className="flex-1 text-left min-w-0"
                      data-testid={`option-group-templates-pick-${tpl.id}`}
                    >
                      <div className="text-sm font-medium truncate">{tpl.name}</div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {tpl.options.slice(0, 3).map((o) => o.name).join(", ")}
                        {tpl.options.length > 3 ? ` +${tpl.options.length - 3}` : ""}
                      </div>
                    </button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      onClick={(e) => { e.stopPropagation(); handleEdit(tpl); }}
                      data-testid={`option-group-templates-edit-${tpl.id}`}
                    >
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-destructive"
                      onClick={(e) => { e.stopPropagation(); handleDelete(tpl); }}
                      data-testid={`option-group-templates-delete-${tpl.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <TemplateEditorDialog
        open={editorOpen}
        template={editing}
        onClose={() => { setEditorOpen(false); setEditing(null); }}
      />
    </>
  );
}

interface TemplateEditorDialogProps {
  open: boolean;
  template: OptionGroupTemplate | null;
  onClose: () => void;
}

function TemplateEditorDialog({ open, template, onClose }: TemplateEditorDialogProps) {
  const [name, setName] = useState("");
  const [selectionType, setSelectionType] = useState<"single" | "multiple">("single");
  const [maxSelections, setMaxSelections] = useState("");
  const [minSelections, setMinSelections] = useState("");
  const [required, setRequired] = useState(false);
  const [onePerOption, setOnePerOption] = useState(false);
  const [priceMode, setPriceMode] = useState<TemplatePriceMode>("sum");
  const [optionType, setOptionType] = useState<TemplateOptionType>("personalizado");
  const [options, setOptions] = useState<{ name: string; price: string; ingredientId?: string }[]>([]);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    if (open && template) {
      setName(template.name);
      setSelectionType(template.selectionType);
      setMaxSelections(template.maxSelections ? String(template.maxSelections) : "");
      setMinSelections(template.minSelections ? String(template.minSelections) : "");
      setRequired(template.required);
      setOnePerOption(template.onePerOption ?? false);
      setPriceMode(template.priceMode || "sum");
      setOptionType(template.optionType || "personalizado");
      setOptions(template.options.map((o) => ({
        name: o.name,
        price: String(o.price ?? 0),
        ingredientId: o.ingredientId ?? undefined,
      })));
    }
  }, [open, template]);

  const updateMutation = useMutation({
    mutationFn: (payload: any) =>
      customFetch(`/api/option-group-templates/${template!.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({ title: "Template atualizado!" });
      onClose();
    },
    onError: (err: any) => {
      toast({ title: "Erro ao salvar", description: extractApiErrorMessage(err, "Tente novamente."), variant: "destructive" });
    },
  });

  const handleSave = () => {
    const cleanedOptions = options
      .map((o) => ({
        name: o.name.trim(),
        price: parseFloat(o.price.replace(",", ".")) || 0,
        ingredientId: optionType === "ingredientes" ? (o.ingredientId || null) : null,
      }))
      .filter((o) => optionType === "ingredientes" ? !!o.ingredientId : o.name.length > 0);
    if (!name.trim()) {
      toast({ title: "Nome obrigatório", variant: "destructive" });
      return;
    }
    if (cleanedOptions.length === 0) {
      toast({ title: "Adicione ao menos uma opção", variant: "destructive" });
      return;
    }
    updateMutation.mutate({
      name: name.trim(),
      selectionType,
      maxSelections: selectionType === "multiple" && maxSelections ? parseInt(maxSelections, 10) : null,
      minSelections: selectionType === "multiple" && required && minSelections ? parseInt(minSelections, 10) : null,
      required,
      onePerOption: selectionType === "multiple" ? onePerOption : false,
      priceMode: selectionType === "multiple" ? priceMode : "sum",
      optionType,
      options: cleanedOptions,
    });
  };

  if (!template) return null;

  return (
    <ModalShell
      open={open}
      onOpenChange={(v) => { if (!v) onClose(); }}
      title="Editar template de grupo"
      size="md"
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="button" onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? "Salvando..." : "Salvar template"}
          </Button>
        </>
      }
    >
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            A edição afeta apenas usos futuros deste template. Produtos que já o usaram mantêm a versão copiada na ocasião.
          </p>
          <div>
            <Label className="text-xs">Nome do grupo</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 text-sm" />
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              className="h-8 rounded-md border border-input bg-background px-2 text-xs"
              value={selectionType}
              onChange={(e) => setSelectionType(e.target.value as "single" | "multiple")}
            >
              <option value="single">Escolher apenas uma</option>
              <option value="multiple">Escolher mais de uma</option>
            </select>
            <select
              className="h-8 rounded-md border border-input bg-background px-2 text-xs"
              value={optionType}
              onChange={(e) => setOptionType(e.target.value as TemplateOptionType)}
              title="Como as opções deste template são definidas."
            >
              <option value="ingredientes">Ingredientes</option>
              <option value="personalizado">Personalizado</option>
            </select>
            {selectionType === "multiple" && (
              <select
                className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                value={priceMode}
                onChange={(e) => setPriceMode(e.target.value as TemplatePriceMode)}
                title="Como o preço do grupo é cobrado quando o cliente escolhe mais de uma opção."
              >
                <option value="sum">Somar valores</option>
                <option value="highest">Apenas o mais caro</option>
                <option value="average">Média dos selecionados</option>
              </select>
            )}
            {selectionType === "multiple" && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">Máx:</span>
                <Input
                  type="number"
                  min="1"
                  value={maxSelections}
                  onChange={(e) => setMaxSelections(e.target.value)}
                  className="w-16 h-8 text-xs"
                  placeholder="∞"
                />
              </div>
            )}
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={required}
                onChange={(e) => {
                  setRequired(e.target.checked);
                  // Desligar obrigatório apaga o piso: regra escondida
                  // continuaria valendo no servidor.
                  if (!e.target.checked) setMinSelections("");
                }}
                className="accent-primary w-3.5 h-3.5"
              />
              <span className="text-xs">Obrigatório</span>
            </label>
            {selectionType === "multiple" && required && (
              <div className="flex items-center gap-1" title="Quantas opções o cliente precisa escolher pra fechar o grupo.">
                <span className="text-xs text-muted-foreground">Mín:</span>
                <Input
                  type="number"
                  min="1"
                  value={minSelections}
                  onChange={(e) => setMinSelections(e.target.value)}
                  className="w-16 h-8 text-xs"
                  placeholder="1"
                />
              </div>
            )}
            {selectionType === "multiple" && (
              <label className="flex items-center gap-1.5 cursor-pointer" title="Cliente não repete a mesma opção — o contador vira liga/desliga.">
                <input
                  type="checkbox"
                  checked={onePerOption}
                  onChange={(e) => setOnePerOption(e.target.checked)}
                  className="accent-primary w-3.5 h-3.5"
                />
                <span className="text-xs">1 de cada</span>
              </label>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Opções</Label>
            {options.map((opt, oi) => (
              <div key={oi} className="flex items-center gap-2">
                {optionType === "ingredientes" ? (
                  <IngredientSelectField
                    value={opt.name}
                    ingredientId={opt.ingredientId}
                    onChange={({ name, ingredientId }) =>
                      setOptions(options.map((o, i) => (i === oi ? { ...o, name, ingredientId } : o)))
                    }
                    // O editor de template não tem "fechar e abrir popup" — só
                    // re-dispara a query (o popup gestão também invalida o
                    // mesmo queryKey, então mudanças aparecem aqui).
                    onEditClick={() => {}}
                    className="flex-1"
                  />
                ) : (
                  <Input
                    placeholder="Nome da opção"
                    value={opt.name}
                    onChange={(e) =>
                      setOptions(options.map((o, i) => (i === oi ? { ...o, name: e.target.value } : o)))
                    }
                    className="flex-1 h-8 text-sm"
                  />
                )}
                <CurrencyInput
                  value={opt.price}
                  onChange={(v) =>
                    setOptions(options.map((o, i) => (i === oi ? { ...o, price: v } : o)))
                  }
                  className="w-24 h-8 text-sm"
                  placeholder="R$ 0,00"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive shrink-0"
                  onClick={() => setOptions(options.filter((_, i) => i !== oi))}
                  disabled={options.length <= 1}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              onClick={() => setOptions([...options, { name: "", price: "0", ingredientId: undefined }])}
            >
              <Plus className="w-3 h-3 mr-1" /> Adicionar opção
            </Button>
          </div>
        </div>
    </ModalShell>
  );
}

export function previewSummary(opts: TemplateOptionItem[]): string {
  const names = opts.slice(0, 3).map((o) => o.name).join(", ");
  return opts.length > 3 ? `${names} +${opts.length - 3}` : names;
}

export { formatCurrency };
